/**
 * "Your booking": the price summary the flow shows from page 2 on (batch-29, item R9; plan §6). Every figure is the
 * server's — this component adds nothing up. On a wide screen it is a column that stays in view; on a phone it is a bar
 * fixed at the bottom ("Total … · Details") that opens the same list in a sheet. While a new price is being fetched
 * the last figures are dimmed. The page that shows it leaves room under its buttons (`SUMMARY_PAGE_PADDING`).
 */
import { useState, useSyncExternalStore } from 'react';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/Dialog';
import { cn } from '@/lib/cn';
import { dateRange, money, plural } from '@/lib/format';
import { RoomIcons } from './RoomIcons';
import { extraIcons, type RoomIcon } from './roomIcons';

/** One priced line. `cents` 0 reads "Free" (a cot or crib at no charge is still shown). */
export interface SummaryLine { label: string; cents: number }
/** One room: who is in it, what it costs, and its cot and crib lines. */
/** `cents` is null on an event that does not charge rooms: the room is listed with no figure. */
/** `cots` / `cribs` are how many the server priced the room with: the room's line shows an icon for each (item BI1). */
export interface SummaryRoom { name: string; people: string; cents: number | null; lines: SummaryLine[]; cots?: number; cribs?: number }
export interface BookingSummary {
  arrive: string; leave: string; nights: number;
  rooms: SummaryRoom[]; extras: SummaryLine[]; fees: SummaryLine[]; taxes: SummaryLine[];
  totalCents: number; dueTodayCents: number; currency?: string;
  /** Page 5: what a voucher the guest applied pays of today's charge; `dueTodayCents` is then what the card still pays (owner, 2026-10-18). */
  voucherCents?: number;
}

/** `icons` is on a room's line only, and only while the room holds a cot or a crib. */
export interface SummaryRow { kind: 'dates' | 'room' | 'people' | 'bed' | 'extra' | 'fee' | 'tax' | 'total' | 'voucher' | 'due'; label: string; amount: string | null; icons?: RoomIcon[] }

/** The summary as the rows it shows, top to bottom: dates and nights, each room with its people and its cot and crib lines, extras, fees, tax, Total, Due today. */
export function summaryRows(s: BookingSummary): SummaryRow[] {
  const cur = s.currency ?? 'USD';
  const amount = (cents: number) => money(cents, cur);
  const priced = (kind: SummaryRow['kind']) => (l: SummaryLine): SummaryRow => ({ kind, label: l.label, amount: amount(l.cents) });
  return [
    { kind: 'dates', label: `${dateRange(s.arrive, s.leave).replace(' – ', ' to ')} · ${plural(s.nights, 'night')}`, amount: null },
    ...s.rooms.flatMap((r): SummaryRow[] => [
      { kind: 'room', label: r.name, amount: r.cents === null ? null : amount(r.cents), ...(extraIcons(r).length ? { icons: extraIcons(r) } : {}) },
      { kind: 'people', label: r.people, amount: null },
      ...r.lines.map((l): SummaryRow => ({ kind: 'bed', label: l.label, amount: l.cents === 0 ? 'Free' : amount(l.cents) })),
    ]),
    ...s.extras.map(priced('extra')), ...s.fees.map(priced('fee')), ...s.taxes.map(priced('tax')),
    { kind: 'total', label: 'Total', amount: amount(s.totalCents) },
    ...(s.voucherCents ? [{ kind: 'voucher' as const, label: 'Voucher', amount: amount(-s.voucherCents) }] : []),
    { kind: 'due', label: 'Due today', amount: amount(s.dueTodayCents) },
  ];
}

/** Bottom padding for a page that shows the summary, so the phone's bar never covers its buttons. */
export const SUMMARY_PAGE_PADDING = 'pb-24 lg:pb-0';

function Rows({ rows, pending }: { rows: SummaryRow[]; pending: boolean }) {
  return (
    <dl className="flex flex-col gap-1.5 text-sm" data-testid="summary-rows">
      {rows.map((r, i) => (
        <div key={i} data-summary-row={r.kind}
          className={cn('flex items-baseline justify-between gap-3', (r.kind === 'people' || r.kind === 'bed') && 'pl-3 text-[13px] text-ink-2',
            r.kind === 'room' && i > 1 && 'mt-1.5', r.kind === 'total' && 'mt-1.5 border-t border-line pt-2 font-semibold text-ink', r.kind === 'due' && 'font-semibold text-ink')}>
          <dt className="min-w-0 break-words">{r.label}{r.icons && <RoomIcons items={r.icons} className="ml-2 inline-flex align-text-bottom text-ink" testid="summary-room-icons" drawn />}</dt>
          <dd className={cn('shrink-0 tabular-nums', pending && 'opacity-70')}>{r.amount}</dd>
        </div>
      ))}
    </dl>
  );
}

/** Where page 5 puts its pay button on a wide screen: under the summary's figures. */
export const SUMMARY_ACTION_ID = 'book-summary-action';
// Where page 2 puts the guest's chosen rooms on a wide screen: under the summary's figures (owner, 2026-10-11).
// The place is told to page 2 the moment it is on the page, so the rooms move once and not at some later render.
let roomsSlot: HTMLElement | null = null;
const roomsSlotListeners = new Set<() => void>();
const setRoomsSlot = (el: HTMLElement | null) => { roomsSlot = el; roomsSlotListeners.forEach((l) => l()); };
const onRoomsSlot = (l: () => void) => { roomsSlotListeners.add(l); return () => { roomsSlotListeners.delete(l); }; };
/** The place under the summary's figures for the chosen rooms, or null while the summary column is not on the page. */
export const useSummaryRoomsSlot = (): HTMLElement | null => useSyncExternalStore(onRoomsSlot, () => roomsSlot, () => null);

export function Summary({ summary, pending = false, due = false, rooms = false }: {
  summary: BookingSummary | null; pending?: boolean;
  /** Page 5: the phone's bar reads "Due today" and the amount to pay now, and the column has room for the pay button. */
  due?: boolean;
  /** Page 2: the chosen rooms stand under the figures, and the column scrolls on its own when it is taller than the screen. */
  rooms?: boolean;
}) {
  const [open, setOpen] = useState(false);
  if (!summary) return null;
  const rows = summaryRows(summary);
  const total = money(due ? summary.dueTodayCents : summary.totalCents, summary.currency ?? 'USD');
  return (
    <>
      <div className={cn('sticky top-4 hidden min-w-0 self-start lg:flex lg:flex-col lg:gap-4', rooms && 'lg:max-h-[calc(100vh_-_2rem)] lg:overflow-y-auto lg:p-px')}>
        <aside className="rounded-panel bg-surface p-5 elevate" aria-label="Your booking" aria-busy={pending || undefined} data-testid="summary-column">
          <h2 className="mb-3 text-[15px] font-semibold text-ink">Your booking</h2>
          <Rows rows={rows} pending={pending} />
          {due && <div id={SUMMARY_ACTION_ID} className="mt-4 empty:hidden" />}
        </aside>
        <div ref={setRoomsSlot} className="min-w-0 empty:hidden" data-testid="summary-rooms" />
      </div>
      <div className="fixed inset-x-0 bottom-0 z-sticky border-t border-line bg-surface px-4 py-2 shadow-lg lg:hidden print:hidden" aria-busy={pending || undefined} data-testid="summary-bar">
        <div className="mx-auto flex w-full max-w-3xl items-center justify-between gap-3">
          <p className="min-w-0 text-sm font-semibold text-ink" data-testid="summary-bar-line">{due ? 'Due today' : 'Total'} <span className={cn('tabular-nums', pending && 'opacity-70')} data-testid="summary-total">{total}</span></p>
          <Button variant="secondary" size="touch" onClick={() => setOpen(true)} data-testid="summary-details">Details</Button>
        </div>
      </div>
      <Dialog open={open} onClose={() => setOpen(false)} title="Your booking" footer={<Button variant="primary" size="touch" onClick={() => setOpen(false)}>Close</Button>}>
        <Rows rows={rows} pending={pending} />
      </Dialog>
    </>
  );
}
